import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomDynamicFormState } from "@midland-industries/Store/Data/DynamicForms/DynamicFormReducer";
import setDynamicFormFieldValue from "@midland-industries/Store/Data/DynamicForms/Handlers/SetDynamicFormFieldValue";
import {
    DynamicFormIntegerFieldLabelPositions,
    DynamicFormIntegerFieldLabelPositionsLeft,
    DynamicFormIntegerFieldLabelPositionsNone,
    DynamicFormIntegerFieldLabelPositionsTop,
} from "@midland-industries/Types/DynamicFormModels";
import { determineFormFieldIndexes } from "@midland-industries/Utilities/DetermineFormFieldIndexes";
import { DynamicFormContext } from "@midland-industries/Widgets/DynamicForms/DynamicForm";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    key = "key",
    labelPosition = "labelPosition",
    label = "label",
    isRequired = "isRequired",
    hintText = "hintText",
    min = "min",
    max = "max",
}

interface OwnProps {
    fields: {
        [fields.key]: string;
        [fields.labelPosition]: DynamicFormIntegerFieldLabelPositions;
        [fields.label]: string;
        [fields.isRequired]: boolean;
        [fields.hintText]: string;
        [fields.min]: number;
        [fields.max]: number;
    };
}

const mapStateToProps = (state: CustomDynamicFormState) => {
    const dynamicFormData = state.xcentiumDynamicForms;
    return {
        dynamicFormData,
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface DynamicFormTextFieldStyles {
    wrapper?: InjectableCss;
    textField?: TextFieldPresentationProps;
}

export const dynamicFormTextFieldStyles: DynamicFormTextFieldStyles = {
    wrapper: {
        css: css`
            width: 100%;
            align-items: center;
        `,
    },
};

const styles = dynamicFormTextFieldStyles;

const DynamicFormTextField: React.FC<Props> = ({ fields, dynamicFormData, setDynamicFormFieldValue }) => {
    const { key, labelPosition, label, isRequired, hintText, min, max } = fields;

    const defaultIntegerFieldValue: number = min;

    const [integerFieldValue, setIntegerFieldValue] = useState<number | null>(defaultIntegerFieldValue);
    const [integerFieldErrorMessage, setIntegerFieldErrorMessage] = useState("");
    const [fieldIndex, setFieldIndex] = useState(-1);
    const [formIndex, setFormIndex] = useState(-1);

    const dynamicFormContext = useContext(DynamicFormContext);

    useEffect(() => {
        initializeIntegerFieldWidget();
    }, [dynamicFormData?.forms]);

    useEffect(() => {
        if (formIndex === -1 || fieldIndex === -1) {
            return;
        }
        if (!integerFieldValue) {
            setIntegerFieldValue(min);
            return;
        }

        if (integerFieldValue > max) {
            setIntegerFieldValue(max);
        }

        if (integerFieldValue < min) {
            setIntegerFieldValue(min);
        }
    }, [min, max]);

    useEffect(() => {
        const { isValid, errorMessage } = ValidateIntegerFieldValue();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label: labelPosition === DynamicFormIntegerFieldLabelPositionsNone ? key : label,
                isRequired,
                value: integerFieldValue,
                type: "DynamicFormIntegerField",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    }, [integerFieldValue]);

    useEffect(() => {
        if (integerFieldValue !== dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value) {
            if (dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value === undefined) {
                setIntegerFieldValue(defaultIntegerFieldValue);
            } else {
                setIntegerFieldValue(dynamicFormData?.forms[formIndex]?.fields[fieldIndex]?.value);
            }
        }
    }, [dynamicFormData?.forms[formIndex]?.fields[fieldIndex]]);

    const initializeIntegerFieldWidget = () => {
        const formFieldIndexes = determineFormFieldIndexes(dynamicFormContext.formName, key, dynamicFormData);
        setFormIndex(formFieldIndexes.formIndex);
        setFieldIndex(formFieldIndexes.fieldIndex);

        // No form registered in state
        if (formFieldIndexes.formIndex < 0) {
            return;
        }

        // Widget already registered in state
        if (formFieldIndexes.formIndex > -1 && formFieldIndexes.fieldIndex > -1) {
            return;
        }

        const { isValid, errorMessage } = ValidateIntegerFieldValue();
        setDynamicFormFieldValue({
            formName: dynamicFormContext.formName,
            formField: {
                key,
                group: "",
                label: labelPosition === DynamicFormIntegerFieldLabelPositionsNone ? key : label,
                isRequired,
                value: defaultIntegerFieldValue,
                type: "DynamicFormIntegerField",
                sendOnlyIfChecked: false,
                hasError: !isValid,
                error: errorMessage,
            },
        });
    };

    const ValidateIntegerFieldValue = () => {
        let errorMessage = isRequired && !integerFieldValue ? (siteMessage("Field_Required", label) as string) : "";

        if (integerFieldValue && integerFieldValue < min) {
            errorMessage = siteMessage("DynamicForms_IntegerField_Min", min.toString()) as string;
        }
        if (integerFieldValue && integerFieldValue > max) {
            errorMessage = siteMessage("DynamicForms_IntegerField_Max", max.toString()) as string;
        }

        setIntegerFieldErrorMessage(errorMessage);

        return {
            isValid: !errorMessage,
            errorMessage,
        };
    };

    const integerFieldChangeHandler = (event: React.FormEvent<HTMLInputElement>) => {
        const newValue = event.currentTarget.value;
        const newNumber = Number(String(newValue));
        setIntegerFieldValue(newNumber);
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === ".") {
            event.preventDefault();
        }
    };

    const onInput = (event: React.FormEvent<HTMLInputElement>) => {
        event.currentTarget.value = event.currentTarget.value.replace(/[^0-9]*/g, "");
    };

    const getDisplayValidationErrors = () => {
        if (formIndex < 0) {
            return false;
        }

        return dynamicFormData?.forms[formIndex]?.displayValidationErrors;
    };

    type FinalLabelPosition = "left" | "top" | undefined;
    const FinalLabelPositionLeft: FinalLabelPosition = "left";
    const FinalLabelPositionTop: FinalLabelPosition = "top";
    const FinalLabelPositionUndefined: FinalLabelPosition = undefined;

    const determineFinalLabelPosition = () => {
        let returnValue: FinalLabelPosition = FinalLabelPositionUndefined;
        switch (labelPosition) {
            case DynamicFormIntegerFieldLabelPositionsLeft:
                returnValue = FinalLabelPositionLeft;
                break;
            case DynamicFormIntegerFieldLabelPositionsTop:
                returnValue = FinalLabelPositionTop;
                break;
            case DynamicFormIntegerFieldLabelPositionsNone:
                returnValue = FinalLabelPositionUndefined;
                break;
            default:
                returnValue = FinalLabelPositionTop;
                break;
        }
        return returnValue;
    };

    const finalLabelPosition: FinalLabelPosition = determineFinalLabelPosition();

    return (
        <StyledWrapper {...styles.wrapper}>
            <TextField
                id={key}
                className="input"
                type="number"
                onKeyDown={onKeyDown}
                onInput={onInput}
                step="1"
                value={integerFieldValue || undefined}
                {...styles.textField}
                labelPosition={finalLabelPosition}
                label={labelPosition !== DynamicFormIntegerFieldLabelPositionsNone ? label : undefined}
                required={isRequired}
                placeholder={hintText}
                error={getDisplayValidationErrors() && integerFieldErrorMessage}
                onChange={integerFieldChangeHandler}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(DynamicFormTextField);
